import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { keyframes } from '@mui/system';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import ExtensionIcon from '@mui/icons-material/Extension';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px rgba(99, 102, 241, 0.4);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.7), 0 0 25px rgba(99, 102, 241, 0.3);
    transform: scale(1.02);
  }
`;

const navItems = [
  { label: '每日观点', path: '/', icon: <HomeIcon /> },
  { label: '标的追踪', path: '/ticker-tracking', icon: <TrendingUpIcon /> },
  { label: '博主追踪', path: '/channel-tracking', icon: <PersonIcon /> },
  { label: '期权异动', path: '/options-flow', icon: <ShowChartIcon /> },
  { label: '功能提案', path: '/features', icon: <LightbulbOutlinedIcon /> },
  { label: 'FOCI', path: '/foci', icon: <ExtensionIcon />, special: true },
];

export default function Header() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isFeatures = location.pathname === '/features';
  const isOptionsFlow = location.pathname === '/options-flow';
  const isFoci = location.pathname === '/foci';
  const isChannelTracking = location.pathname === '/channel-tracking' ||
    location.pathname.startsWith('/channel/');
  const isTickerTracking = location.pathname === '/ticker-tracking' ||
    (location.pathname.length > 1 &&
      !/^\d{8}$/.test(location.pathname.slice(1)) &&
      !isFeatures &&
      !isOptionsFlow &&
      !isChannelTracking &&
      !isFoci);
  const isHome = !isFeatures && !isOptionsFlow && !isTickerTracking && !isChannelTracking && !isFoci;

  const isActive = (path: string) => {
    if (path === '/') return isHome;
    if (path === '/ticker-tracking') return isTickerTracking;
    if (path === '/channel-tracking') return isChannelTracking;
    if (path === '/options-flow') return isOptionsFlow;
    if (path === '/features') return isFeatures;
    if (path === '/foci') return isFoci;
    return false;
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await signOut();
  };

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="start"
              onClick={toggleDrawer(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              fontWeight: 700,
              textDecoration: 'none',
              color: 'inherit',
              mr: 3,
              fontSize: isMobile ? '1rem' : '1.25rem',
            }}
          >
            AlphaMoeMoe
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  startIcon={item.path !== '/' ? item.icon : undefined}
                  sx={item.special ? {
                    fontWeight: 700,
                    opacity: 1,
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                    borderRadius: 2,
                    animation: isFoci ? 'none' : `${pulseGlow} 2s ease-in-out infinite`,
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
                      animation: 'none',
                    },
                  } : {
                    fontWeight: isActive(item.path) ? 600 : 400,
                    opacity: isActive(item.path) ? 1 : 0.7,
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Spacer for mobile */}
          {isMobile && <Box sx={{ flexGrow: 1 }} />}

          {user && (
            <Box>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar
                  src={user.user_metadata?.avatar_url}
                  alt={user.email || ''}
                  sx={{ width: 32, height: 32 }}
                />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              AlphaMoeMoe
            </Typography>
          </Box>
          <Divider />
          <List>
            {navItems.map((item) => (
              <ListItemButton
                key={item.path}
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
                sx={item.special ? {
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)',
                  },
                } : {}}
              >
                <ListItemIcon sx={item.special ? { color: 'primary.main' } : {}}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: item.special ? 700 : (isActive(item.path) ? 600 : 400),
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
